/**
 * iter964 — Staff Food dialog. Reuses the current POS cart + the existing
 * employee list. Submits to /cashier/staff-food which forces price/revenue to
 * 0, records staff member + branch + operator, and still deducts inventory.
 * Kept OUT of normal sales totals (separate collection).
 */
import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SearchableSelect } from '@/components/SearchableSelect';
import { UtensilsCrossed, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function StaffFoodDialog({ open, onOpenChange, cart = [], branchId, onSubmitted }) {
  const [staff, setStaff] = useState([]);
  const [staffId, setStaffId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get('/cashier/staff').then((r) => setStaff(r.data || [])).catch(() => setStaff([]));
  }, [open]);

  // Backend already normalises name + filters to active staff.
  const staffOptions = useMemo(() => staff || [], [staff]);

  const totalQty = cart.reduce((s, c) => s + (c.quantity || 1), 0);

  const submit = async () => {
    if (!staffId) { toast.error('Select the staff member'); return; }
    if (!cart.length) { toast.error('Cart is empty'); return; }
    setSubmitting(true);
    try {
      const staffName = staffOptions.find((e) => e.id === staffId)?.name || '';
      const res = await api.post('/cashier/staff-food', {
        branch_id: branchId || null,
        staff_member_id: staffId,
        staff_member_name: staffName,
        items: cart.map((c) => ({ item_id: c.item_id, quantity: c.quantity || 1 })),
      });
      toast.success(`Staff food logged (${res.data?.ref || ''}) — stock updated, SAR 0 charged`);
      onSubmitted?.();
      setStaffId('');
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to log staff food');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="staff-food-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UtensilsCrossed size={18} className="text-amber-600" /> Staff Food
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            The {cart.length} item(s) / {totalQty} unit(s) in the cart will be recorded as free staff meal
            (SAR 0, no revenue) and deducted from inventory.
          </p>
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase block mb-1">Staff member</label>
            <SearchableSelect
              items={staffOptions}
              value={staffId}
              onChange={setStaffId}
              searchKeys={['name', 'position']}
              placeholder="Search staff by name…"
              renderItem={(e) => (
                <div className="flex items-center justify-between w-full gap-2">
                  <span className="truncate">{e.name}</span>
                  {e.position && <span className="text-[10px] text-muted-foreground shrink-0">{e.position}</span>}
                </div>
              )}
              data-testid="staff-food-member"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting || !cart.length} data-testid="staff-food-confirm" className="bg-amber-600 hover:bg-amber-700">
            {submitting ? <Loader2 size={14} className="animate-spin mr-1" /> : <UtensilsCrossed size={14} className="mr-1" />}
            Record Staff Food
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
